import { Link, useLocation } from "react-router-dom";
import { Bell, Home, Menu, ScanLine, Search } from "lucide-react";
import { badgeText, notificationCentreApi, NOTIFY_QK } from "../../api/notificationCentreApi";
import { useShownRoles } from "../../auth/useShownRoles";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useLiveConnectionState } from "../../hooks/useLiveUpdates";
import { hubsFor } from "../hub/hubs";
import { openHeaderPanel } from "../header/headerStore";
import { BELL_POLL_MS } from "../notifications/NotificationBell";
import { openPhoneSheet, usePhoneSheet } from "./phoneState";

// Release 1.5.0 phone mode: the bottom tab bar — Home, Scan, Search, Notifications, More. Search opens the header's own
// search sheet (header-1-5) and Notifications opens the notification centre with the bell's unread count (notify-1-5);
// nothing is duplicated. Every target is at least 56 px tall and a fifth of the width.

const TAB = "flex min-h-[56px] w-full flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500";

function tone(active: boolean): string {
  return active ? "text-brand-700" : "text-slate-600 hover:text-slate-900";
}

export function BottomTabBar() {
  const { pathname } = useLocation();
  const roles = useShownRoles();
  const sheet = usePhoneSheet();
  const live = useLiveConnectionState().status === "live";
  const count = useApiQuery([...NOTIFY_QK.count], notificationCentreApi.count, { refetchInterval: live ? false : BELL_POLL_MS });
  const unread = count.data?.unreadCount ?? 0;
  const badge = badgeText(unread);
  const home = hubsFor(roles)[0]?.to ?? "/";
  const onNotifications = pathname.startsWith("/my/notifications");
  const onHome = !onNotifications && sheet === null && (pathname === "/" || pathname === home || pathname === "/home");

  return (
    <nav
      aria-label="Phone navigation"
      className="fixed inset-x-0 bottom-0 z-[45] border-t border-slate-200 bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_8px_rgba(15,23,42,0.06)] backdrop-blur print:hidden"
      data-testid="phone-tab-bar"
    >
      <ul className="mx-auto grid max-w-xl grid-cols-5">
        <li>
          <Link to="/" className={`${TAB} ${tone(onHome)}`} aria-current={onHome ? "page" : undefined} data-testid="phone-tab-home">
            <Home aria-hidden="true" className="h-5 w-5" />
            Home
          </Link>
        </li>
        <li>
          <button type="button" className={`${TAB} ${tone(sheet?.kind === "scan")}`} onClick={() => openPhoneSheet({ kind: "scan" })} data-testid="phone-tab-scan">
            <ScanLine aria-hidden="true" className="h-5 w-5" />
            Scan
          </button>
        </li>
        <li>
          <button type="button" className={`${TAB} ${tone(false)}`} onClick={() => openHeaderPanel("search")} data-testid="phone-tab-search">
            <Search aria-hidden="true" className="h-5 w-5" />
            Search
          </button>
        </li>
        <li>
          <Link
            to="/my/notifications"
            className={`${TAB} ${tone(onNotifications)}`}
            aria-current={onNotifications ? "page" : undefined}
            aria-label={unread === 0 ? "Notifications, none unread" : `Notifications, ${unread} unread`}
            data-testid="phone-tab-notifications"
          >
            <span className="relative">
              <Bell aria-hidden="true" className="h-5 w-5" />
              {badge && (
                <span
                  className="absolute -right-2.5 -top-1.5 min-w-[1.1rem] rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white"
                  aria-hidden="true"
                  data-testid="phone-tab-notifications-badge"
                >
                  {badge}
                </span>
              )}
            </span>
            Notifications
          </Link>
        </li>
        <li>
          <button
            type="button"
            className={`${TAB} ${tone(sheet?.kind === "more" || sheet?.kind === "report")}`}
            onClick={() => openPhoneSheet({ kind: "more" })}
            aria-haspopup="dialog"
            data-testid="phone-tab-more"
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
            More
          </button>
        </li>
      </ul>
    </nav>
  );
}
